import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleIdentityScript, isGoogleIdentityAvailable } from '../../utils/googleIdentity';
import { GOOGLE_SIGNIN_USER_MESSAGE } from '../../utils/errorHandling';

const GOOGLE_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();

export function GoogleSignInButton({ onCredential, onError, text = 'continue_with', disabled = false }) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let mounted = true;

    async function setup() {
      if (!GOOGLE_CLIENT_ID) {
        return;
      }
      try {
        await loadGoogleIdentityScript();
        if (!mounted || !containerRef.current || !isGoogleIdentityAvailable()) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => {
            const credential = (resp?.credential || '').trim();
            if (!credential) {
              onErrorRef.current?.('Google sign-in was canceled.');
              return;
            }
            onCredentialRef.current?.(credential);
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        containerRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(containerRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text,
          width: 320,
        });
        if (mounted) {
          setReady(true);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[MyLegacyBridge] Google Identity Services failed to load:', error);
        onErrorRef.current?.(GOOGLE_SIGNIN_USER_MESSAGE);
      }
    }

    setup();
    return () => {
      mounted = false;
      setReady(false);
      try {
        window.google?.accounts?.id?.cancel?.();
      } catch (_e) {
        // GIS may not be loaded yet.
      }
    };
  }, [text]);

  return (
    <div className="w-full">
      <div className={disabled ? 'pointer-events-none opacity-60' : ''} ref={containerRef} />
      {!ready && (
        <p className="mt-2 text-center text-xs text-[#8b7355]">
          {GOOGLE_CLIENT_ID ? 'Loading Google sign-in…' : 'Google sign-in is unavailable right now.'}
        </p>
      )}
    </div>
  );
}
